import { isFunction } from '@sonar-software/sonar-ts-lib';
import { usePrevious } from './usePrevious';

import { useCallback, useEffect, useRef, useState } from 'react';

export type QueryParams = Record<string | number | symbol, unknown>;
export type QueryParamsUpdate<P> = Partial<P> | ((params: P) => Partial<P>);
export type UpdateQueryParams<P> = (newParams: QueryParamsUpdate<P>) => void;
export type OnUpdateQueryParams<P> = (newParams: Partial<P>, prev: P | undefined, combined: P) => void;
export type TransformQueryParams<P> = (newParams: Partial<P>, prev: P, combined: P) => Partial<P>;

export type UseQueryParamsOptions<P> = {
	defaultParams?: P;
	fallbackParams?: Partial<P>;
	onUpdate?: OnUpdateQueryParams<P>;
	transform?: TransformQueryParams<P>;
};

export interface UseQueryParamsResult<P> {
	queryParams: P;
	updateParams: UpdateQueryParams<P>;
}

export function useQueryParams<P>(options?: UseQueryParamsOptions<P>): UseQueryParamsResult<P> {
	const optionsRef = useRef(options);

	optionsRef.current = options;

	const [ queryParams, setParams ] = useState<P>(options?.defaultParams || ({} as P));
	const [ newQueryParams, setNewQueryParams ] = useState({} as Partial<P>);
	const previousQueryParams = usePrevious<P>(queryParams, options?.defaultParams || ({} as P));

	useEffect(() => {
		if (optionsRef.current?.onUpdate && queryParams !== previousQueryParams) {
			optionsRef.current.onUpdate(newQueryParams, previousQueryParams, queryParams);
		}
	}, [ newQueryParams, queryParams, previousQueryParams ]);

	const updateParams = useCallback(
		(paramsUpdate: QueryParamsUpdate<P>) => {
			const newParams = isFunction(paramsUpdate) ? paramsUpdate(queryParams) : paramsUpdate;
			const { fallbackParams, transform } = optionsRef.current || {};

			setNewQueryParams(newParams);

			setParams((prev) => {
				const combined = {
					...prev,
					...fallbackParams,
					...newParams
				};

				return {
					...combined,
					...transform ? transform(newParams, queryParams, combined) : {}
				};
			});
		},
		[ queryParams ]
	);

	return {
		queryParams,
		updateParams
	};
}
